import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getCategoryBySlug, getProductsBySlug } from "../../utils/storage";

export default function CategoryPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const cardRefs = useRef([]);

    const [category, setCategory] = useState(null);
    const [products, setProducts] = useState([]);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        const cat = getCategoryBySlug(slug);
        if (!cat) { setNotFound(true); return; }
        const prods = getProductsBySlug(slug);
        setCategory(cat);
        setProducts(prods);
        const titleCase = (str) =>
            str.replace(
                /\b\w/g,
                char => char.toUpperCase()
            );

        document.title = `${titleCase(cat.name)} | Tecniqa`;
    }, [slug]);

    useEffect(() => {
        const observers = cardRefs.current.map((el, i) => {
            if (!el) return null;
            const obs = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setTimeout(() => el.classList.add("cp-visible"), i * 90);
                        obs.disconnect();
                    }
                },
                { threshold: 0.1 }
            );
            obs.observe(el);
            return obs;
        });
        return () => observers.forEach((o) => o?.disconnect());
    }, [products]);

    if (notFound) {
        return (
            <div className="cp-not-found">
                <h2>Category not found</h2>
                <button onClick={() => navigate(-1)}>Go Back</button>
            </div>
        );
    }

    if (!category) return null;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
                @import url('https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css');

                :root {
                    --dark: #1B211A;
                    --green: #628141;
                    --greenSoft: #8BAE66;
                    --white: #ffffff;
                }

                /* ── FULL PAGE OVERRIDE ──────────────────────────────────────────
                   Force the light green background on the page wrapper AND its
                   immediate parent so the layout's dark background doesn't bleed
                   through the hero area (which is what Refrigerant.jsx avoids
                   because it's a standalone route with no dark wrapper above it).
                ────────────────────────────────────────────────────────────────── */
                .cp-page-shell {
                    background: linear-gradient(180deg, #eef6ee 0%, #e4f0e3 60%, #f7faf7 100%) !important;
                    min-height: 100vh;
                    margin-top: -1px;
                    padding-top: 1px;
                }

                .cp-page {
                    font-family: 'DM Sans', sans-serif;
                    min-height: 100vh;
                    background: transparent;
                    padding: 0 0 80px;
                    position: relative;
                    overflow: hidden;
                    isolation: isolate;
                }

                .cp-blob {
                    position: absolute;
                    border-radius: 50%;
                    filter: blur(100px);
                    pointer-events: none;
                }
                .cp-blob-1 {
                    width: 480px; height: 480px;
                    top: -160px; right: -100px;
                    background: rgba(98,129,65,0.10);
                }
                .cp-blob-2 {
                    width: 320px; height: 320px;
                    bottom: -80px; left: -60px;
                    background: rgba(139,174,102,0.10);
                }
                .cp-page-grid {
                    position: absolute; inset: 0;
                    background-image:
                        linear-gradient(rgba(98,129,65,0.03) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(98,129,65,0.03) 1px, transparent 1px);
                    background-size: 52px 52px;
                    pointer-events: none;
                }

                /* HERO — same background as the page so no visible seam */
                .cp-hero {
                    position: relative; z-index: 1;
                    padding: 32px 8% 52px;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-end;
                    flex-wrap: wrap;
                    gap: 28px;
                    background: transparent;
                    text-align: left;
                }
                .cp-hero > div {
                    text-align: left;
                }
                .cp-eyebrow {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 0.12em;
                    text-transform: uppercase;
                    color: var(--green);
                    margin-bottom: 12px;
                }
                .cp-eyebrow-line {
                    width: 28px; height: 2px;
                    background: var(--green);
                    border-radius: 2px;
                }
                .cp-hero h1 {
                    font-family: 'Barlow Condensed', sans-serif;
                    font-size: clamp(2rem, 4.5vw, 3rem);
                    font-weight: 800;
                    color: var(--dark);
                    line-height: 1.08;
                    letter-spacing: -0.02em;
                    margin: 0 0 14px;
                }
                .cp-hero h1 span { color: var(--green); }
                .cp-hero p {
                    font-size: 15px;
                    color: #5b5b5b;
                    line-height: 1.75;
                    max-width: 460px;
                    margin: 0;
                }

                /* BACK BUTTON */
                .cp-btn-back {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    padding: 10px 20px;
                    border: 1.5px solid #d9d9d9;
                    border-radius: 100px;
                    background: #fff;
                    font-family: 'DM Sans', sans-serif;
                    font-size: 13.5px;
                    font-weight: 600;
                    color: #111;
                    cursor: pointer;
                    transition: all 0.22s ease;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                    flex-shrink: 0;
                    min-height: 44px;
                }
                .cp-btn-back:hover {
                    border-color: var(--green);
                    color: var(--green);
                    background: rgba(98,129,65,0.06);
                    transform: translateX(-3px);
                }

                /* EMPTY STATE */
                .cp-empty {
                    position: relative; z-index: 1;
                    margin: 0 8%;
                    padding: 80px 40px;
                    border: 2px dashed rgba(98,129,65,0.25);
                    border-radius: 24px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 16px;
                    background: rgba(255,255,255,0.5);
                }
                .cp-empty-icon {
                    font-size: 48px;
                    color: rgba(98,129,65,0.3);
                }
                .cp-empty h3 {
                    font-family: 'Barlow Condensed', sans-serif;
                    font-size: 1.6rem;
                    font-weight: 800;
                    color: #888;
                    margin: 0;
                }
                .cp-empty p {
                    font-size: 14px;
                    color: #aaa;
                    margin: 0;
                    text-align: center;
                }

                /* GRID */
                .cp-grid {
                    position: relative; z-index: 1;
                    padding: 0 8%;
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(255px, 1fr));
                    gap: 24px;
                }

                /* CARD */
                .cp-card {
                    border-radius: 20px;
                    overflow: hidden;
                    background: linear-gradient(180deg, #ffffff 0%, #f7f7f7 100%);
                    border: 1px solid rgba(0,0,0,0.06);
                    box-shadow: 0 4px 24px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04);
                    display: flex;
                    flex-direction: column;
                    cursor: pointer;
                    position: relative;
                    opacity: 0;
                    transform: translateY(28px);
                    transition:
                        transform 0.35s cubic-bezier(0.4,0,0.2,1),
                        box-shadow 0.35s cubic-bezier(0.4,0,0.2,1),
                        border-color 0.25s,
                        opacity 0.4s ease;
                }
                .cp-card.cp-visible {
                    opacity: 1;
                    transform: translateY(0);
                }
                .cp-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 0 0 1.5px rgba(98,129,65,0.35);
                    border-color: rgba(98,129,65,0.35);
                }

                .cp-badge {
                    position: absolute;
                    top: 14px; left: 14px; z-index: 3;
                    font-size: 10px;
                    font-weight: 700;
                    letter-spacing: 0.08em;
                    text-transform: uppercase;
                    color: #fff;
                    padding: 4px 12px;
                    border-radius: 100px;
                    background: linear-gradient(135deg, var(--green), var(--greenSoft));
                    box-shadow: 0 3px 10px rgba(98,129,65,0.35);
                }

                .cp-card-actions {
                    position: absolute;
                    top: 14px; right: 14px; z-index: 3;
                    opacity: 0;
                    transform: translateX(10px);
                    transition: all 0.25s cubic-bezier(0.4,0,0.2,1);
                }
                .cp-card:hover .cp-card-actions {
                    opacity: 1; transform: translateX(0);
                }
                .cp-action-btn {
                    width: 34px; height: 34px;
                    border-radius: 10px;
                    border: 1.5px solid rgba(0,0,0,0.08);
                    background: rgba(255,255,255,0.92);
                    backdrop-filter: blur(8px);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 15px; color: #444; cursor: pointer;
                    transition: all 0.18s;
                }
                .cp-action-btn:hover {
                    border-color: var(--green); color: var(--green);
                    background: rgba(98,129,65,0.08);
                    transform: scale(1.1);
                }

                .cp-img-wrap {
                    height: 200px;
                    display: flex; align-items: center; justify-content: center;
                    padding: 24px;
                    background: radial-gradient(circle at 50% 30%, rgba(98,129,65,0.12) 0%, rgba(245,245,245,0.7) 70%, transparent 100%);
                    position: relative; overflow: hidden;
                }
                .cp-img-wrap::after {
                    content: '';
                    position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
                    background: linear-gradient(90deg, transparent, rgba(98,129,65,0.18), transparent);
                }
                .cp-img-wrap img {
                    max-height: 145px; max-width: 100%; object-fit: contain;
                    filter: drop-shadow(0 8px 20px rgba(0,0,0,0.10));
                    transition: transform 0.45s cubic-bezier(0.4,0,0.2,1);
                }
                .cp-card:hover .cp-img-wrap img {
                    transform: scale(1.1) rotate(-1deg);
                    filter: drop-shadow(0 14px 28px rgba(98,129,65,0.18));
                }

                .cp-img-placeholder {
                    width: 80px; height: 80px;
                    border-radius: 50%;
                    background: rgba(98,129,65,0.10);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 32px; color: rgba(98,129,65,0.4);
                }

                .cp-card-body {
                    padding: 18px 18px 0; flex: 1;
                    display: flex; flex-direction: column; gap: 6px;
                }
                .cp-brand {
                    font-size: 10.5px; font-weight: 700;
                    letter-spacing: 0.1em; text-transform: uppercase;
                    color: var(--green);
                }
                .cp-title {
                    font-size: 14px; font-weight: 600; color: #111;
                    line-height: 1.45;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden; flex: 1;
                }
                .cp-stock {
                    display: flex; align-items: center; gap: 5px;
                    font-size: 11.5px; font-weight: 600; color: var(--green); padding-bottom: 4px;
                }
                .cp-stock-dot {
                    width: 6px; height: 6px; border-radius: 50%;
                    background: var(--green);
                    box-shadow: 0 0 6px rgba(98,129,65,0.5);
                }

                .cp-card-footer { padding: 12px 18px 18px; }
                .cp-cta {
                    width: 100%; padding: 11px;
                    border: none; border-radius: 12px;
                    background: #111; color: #fff;
                    font-family: 'DM Sans', sans-serif;
                    font-size: 13px; font-weight: 700; cursor: pointer;
                    display: flex; align-items: center; justify-content: center; gap: 8px;
                    min-height: 44px; letter-spacing: 0.02em;
                    opacity: 0; transform: translateY(8px);
                    transition:
                        opacity 0.28s cubic-bezier(0.4,0,0.2,1),
                        transform 0.28s cubic-bezier(0.4,0,0.2,1),
                        background 0.2s;
                }
                .cp-card:hover .cp-cta { opacity: 1; transform: translateY(0); }
                .cp-cta:hover { background: var(--green); }

                .cp-not-found {
                    min-height: 60vh; display: flex; flex-direction: column;
                    align-items: center; justify-content: center; gap: 16px;
                    background: linear-gradient(180deg, #eef6ee 0%, #e4f0e3 60%, #f7faf7 100%);
                }

                @media (max-width: 900px) {
                    .cp-hero { padding: 32px 6% 36px; }
                    .cp-grid { padding: 0 6%; gap: 18px; }
                }
                @media (max-width: 640px) {
                    .cp-hero { padding: 28px 5% 28px; flex-direction: column; align-items: flex-start; }
                    .cp-grid { padding: 0 5%; grid-template-columns: repeat(auto-fill, minmax(220px,1fr)); gap: 14px; }
                    .cp-cta { opacity: 1; transform: translateY(0); }
                    .cp-card-actions { opacity: 1; transform: translateX(0); }
                }
                @media (max-width: 480px) {
                    .cp-grid { grid-template-columns: 1fr 1fr; gap: 12px; }
                    .cp-card { border-radius: 14px; }
                    .cp-img-wrap { height: 140px; padding: 14px; }
                    .cp-card-body { padding: 12px 12px 0; }
                    .cp-card-footer { padding: 10px 12px 14px; }
                }
            `}</style>

            {/* Shell div overrides any dark parent background */}
            <div className="cp-page-shell">
                <div className="cp-page">
                    <div className="cp-blob cp-blob-1" />
                    <div className="cp-blob cp-blob-2" />
                    <div className="cp-page-grid" />

                    {/* HERO */}
                    <div className="cp-hero">
                        <div>
                            <div className="cp-eyebrow">
                                <div className="cp-eyebrow-line" />
                                {category.eyebrow}
                            </div>
                            <h1>
                                {(() => {
                                    const words = category.name.split(" ");
                                    const last = words.pop();
                                    return (
                                        <>
                                            {words.join(" ")}{words.length > 0 ? " " : ""}
                                            <span>{last}</span>
                                        </>
                                    );
                                })()}
                            </h1>
                            <p>{category.description}</p>
                        </div>

                        <button className="cp-btn-back" onClick={() => window.history.back()}>
                            <i className="ri-arrow-left-line" />
                            Back
                        </button>
                    </div>

                    {/* GRID or EMPTY */}
                    {products.length === 0 ? (
                        <div className="cp-empty">
                            <div className="cp-empty-icon">
                                <i className="ri-box-3-line" />
                            </div>
                            <h3>No Products Yet</h3>
                            <p>
                                Products added to the <strong>{category.name}</strong> category
                                will appear here.
                            </p>
                        </div>
                    ) : (
                        <div className="cp-grid">
                            {products.map((product, i) => (
                                <div
                                    key={product.id}
                                    className="cp-card"
                                    ref={(el) => (cardRefs.current[i] = el)}
                                    onClick={() => navigate(`/category/${slug}/${product.id}`)}
                                >
                                    <div className="cp-badge">{product.badge}</div>

                                    <div className="cp-card-actions">
                                        <button
                                            className="cp-action-btn"
                                            title="View product"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/category/${slug}/${product.id}`);
                                            }}
                                        >
                                            <i className="ri-eye-line" />
                                        </button>
                                    </div>

                                    <div className="cp-img-wrap">
                                        {product.images?.[0] ? (
                                            <img src={product.images[0]} alt={product.name} />
                                        ) : (
                                            <div className="cp-img-placeholder">
                                                <i className="ri-image-line" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="cp-card-body">
                                        <div className="cp-brand">{product.brand}</div>
                                        <div className="cp-title">{product.name}</div>
                                        <div className="cp-stock">
                                            <div className="cp-stock-dot" />
                                            {product.availability} · Fast Shipping
                                        </div>
                                    </div>

                                    <div className="cp-card-footer">
                                        <button
                                            className="cp-cta"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/category/${slug}/${product.id}`);
                                            }}
                                        >
                                            <i className="ri-eye-line" />
                                            View Product
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}